import React, { useState } from "react"
import { Col, Row, Button, Form } from "react-bootstrap"
import { Link, useLocation } from "react-router-dom"
import { Formik } from "formik"
import { string, object, boolean } from "yup"
import { useTranslation, Trans } from "react-i18next"

const validationSchema = (t: (key: string, opts?: Record<string, unknown>) => string) => {
  return object({
    firstName: string()
      .min(2, t("common.validation.invalidMinLength"))
      .max(30, t("common.validation.invalidMaxLength"))
      .required(t("common.validation.required")),
    lastName: string()
      .min(2, t("common.validation.invalidMinLength"))
      .max(30, t("common.validation.invalidMaxLength"))
      .required(t("common.validation.required")),
    email: string().email(t("common.validation.invalidEmail")).required(t("common.validation.required")),
    password: string().min(8, t("common.validation.invalidPasswordMinLength", { min: 8 })).required(t("common.validation.required")),
    terms: boolean().required(t("common.validation.invalidTerms")).oneOf([true], t("common.validation.invalidTerms"))
  })
}

const displayIF = (visible: boolean | string | undefined): React.CSSProperties => {
  return { display: visible ? "block" : "none" }
}

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

interface RegisterProps {
  onSubmit: (values: { firstName: string; lastName: string; email: string; password: string; promocode: string; terms: boolean }) => void
  isLoading: boolean
}

const Register = ({ onSubmit, isLoading }: RegisterProps) => {
  const [promoVisibility, setPromoVisibility] = useState(false)
  const query = useQuery()
  const { t } = useTranslation()

  return (
    <>
      <h1 className="title mb-5">{t("auth.register")}</h1>
      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          password: "",
          promocode: query.get("code") || "",
          email: "",
          terms: false
        }}
        validationSchema={validationSchema(t)}
        validateOnBlur={false}
        onSubmit={values => onSubmit(values)}>
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Row className="my-4">
              <Form.Group as={Col} xs={12} md={6} controlId="RegisterFormFirstname">
                <Form.Control
                  type="text"
                  name="firstName"
                  placeholder={t("auth.form.firstName")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.firstName}
                  isInvalid={!!(touched.firstName && errors.firstName)}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} xs={12} md={6} controlId="RegisterFormLastname">
                <Form.Control
                  type="text"
                  name="lastName"
                  placeholder={t("auth.form.lastName")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.lastName}
                  isInvalid={!!(touched.lastName && errors.lastName)}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.lastName}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} xs={12} controlId="RegisterFormEmail">
                <Form.Control
                  type="email"
                  name="email"
                  placeholder={t("auth.form.email")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  isInvalid={!!(touched.email && errors.email)}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} xs={12} controlId="RegisterFormPassword">
                <Form.Control // TODO: make this hidden
                  type="text"
                  name="password"
                  placeholder={t("auth.form.password")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  isInvalid={!!(touched.password && errors.password)}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} xs={12} controlId="RegisterFormPromoCode" style={displayIF(promoVisibility)}>
                <Form.Control
                  type="text"
                  name="promocode"
                  placeholder={t("auth.form.promocode")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.promocode}
                  isInvalid={!!(touched.promocode && errors.promocode)}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.promocode}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} xs={12} controlId="RegisterFormTerms" className="text-left">
                <Form.Check
                  type="checkbox"
                  name="terms"
                  label={
                    <>
                      <span className="text-dark">{t("auth.form.accept")} </span>
                      <a
                        className="underline"
                        href="https://www.gettips.fi/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer">
                        {t("auth.form.terms")}
                      </a>
                    </>
                  }
                  onChange={handleChange}
                  checked={values.terms}
                  isInvalid={!!(touched.terms && errors.terms)}
                />
                <Form.Control.Feedback tooltip type="invalid" style={displayIF(!!(touched.terms && errors.terms))}>
                  {errors.terms}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            <Row>
              <Col xl={{ span: 8, offset: 2 }} xs={12} style={displayIF(!promoVisibility)}>
                <Button block variant="outline-dark" type="button" onClick={() => setPromoVisibility(true)}>
                  {t("auth.button.promocode")}
                </Button>
              </Col>
            </Row>
            <Row className="mb-4 mt-2">
              <Col xl={{ span: 8, offset: 2 }} xs={12}>
                <Button block variant="primary" type="submit" disabled={isLoading}>
                  {t("auth.button.signup")}
                </Button>
              </Col>
            </Row>
            <Row className="small">
              <Col>
                <Trans i18nKey="auth.button.alreadyAccount">
                  <Link className="underline" to="/login"></Link>
                </Trans>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default Register
